import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Button } from 'components/facets'
import { ConditionBadge } from 'components/facets/ConditionBadge'
import React, { useEffect, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import {
  CONDITIONS_MAP_WITH_LABELS,
  INVENTORY_MULTI_SELECTION_UPDATES,
  TAXATION_SCHEME_LABELS,
} from 'shared'
import { ESCAPE_STRING_FOR_EMPTY_COMMENT, LISTING_AND_APPRAISAL } from 'utils/constants'
import { ConfirmPayload, PriceTarget } from './ActionsPresenter'

type Props = {
  open: boolean
  type: INVENTORY_MULTI_SELECTION_UPDATES | null
  onClose: () => void
  onConfirm: (payload: ConfirmPayload) => void
  commentNeedsUsEditionWarning: (comment: string) => boolean
  showButtonRemoveComment: boolean
  parentPage: string
  showBuyPriceOption?: boolean
}

export const ActionEditorDialog = ({
  open,
  type,
  onClose,
  onConfirm,
  commentNeedsUsEditionWarning,
  showButtonRemoveComment,
  parentPage,
  showBuyPriceOption = false,
}: Props): JSX.Element | null => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [priceTarget, setPriceTarget] = useState<string>('sellPrice')
  // Comment the US Edition warning is currently shown for: confirming the same text again applies it,
  // editing it asks for the warning again
  const [warnedComment, setWarnedComment] = useState<string | null>(null)

  // clean input when open/close
  useEffect(() => {
    if (open) {
      setValue('')
      setWarnedComment(null)
    }
  }, [open, type])

  const isCondition = type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION
  const isLocation = type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION

  const title = useMemo(() => {
    switch (type) {
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE:
        return t('stockPricing.bulkEdit.setPrice')
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY:
        return t('stockPricing.bulkEdit.setQuantity')
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION:
        return t('stockPricing.bulkEdit.setLocation')
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT:
        return t('stockPricing.bulkEdit.setComment')
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION:
        return t('stockPricing.bulkEdit.setCondition')
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME:
        return t('taxation.setTaxationScheme')
      default:
        return ''
    }
  }, [type, t])

  const placeholder = useMemo(() => {
    switch (type) {
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE:
        return '0.25, 10, 90%, +120%...'
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT:
        return t('stockPricing.bulkEdit.commentPlaceholder')
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY:
        return '0, 4, +5, -3...'
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION:
        return 'box, 1A...'
      default:
        return ''
    }
  }, [type, t])

  const commentTriggersUsEditionWarning =
    type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT && commentNeedsUsEditionWarning(value)

  const showUsEditionWarning = commentTriggersUsEditionWarning && warnedComment === value

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!type) return
    if (type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION) return
    if (type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME) {
      if (!value.trim()) return
      onConfirm({ actionType: type, input: value })
      return
    }

    if (type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE) {
      const target: PriceTarget = priceTarget === 'buyPrice' ? 'buy' : 'sell'
      onConfirm({ actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE, target, input: value })
      return
    }

    // Show the warning first and apply the comment only on the next confirmation
    if (commentTriggersUsEditionWarning && warnedComment !== value) {
      setWarnedComment(value)
      return
    }

    onConfirm({ actionType: type, input: value })
  }
  const handleChangeEditPriceType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPriceTarget(event.target.value as 'sellPrice' | 'buyPrice')
  }

  if (!open || !type) return null

  const getContent = () => {
    switch (type) {
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION:
        return (
          <div className="d-flex flex-row flex-wrap justify-content-between">
            {CONDITIONS_MAP_WITH_LABELS.map((condition) => (
              <div
                key={condition.code}
                className="popper-condition-badge"
                data-testid={`set-condition-${condition.name}`}
                onClick={() =>
                  onConfirm({
                    actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION,
                    input: condition.name,
                  })
                }
                style={{ cursor: 'pointer' }}
              >
                <ConditionBadge condition={condition.name} />
              </div>
            ))}
          </div>
        )
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME:
        return (
          <div className="p-2">
            <p className="mb-3">{t('taxation.setTaxationSchemeDescription')}</p>
            <form onSubmit={handleSubmit}>
              <RadioGroup
                // RadioGroup wants strings: I convert the number to a string
                value={value !== undefined ? String(value) : ''}
                onChange={(_, value) => {
                  setValue(value as TAXATION_SCHEME_LABELS)
                }}
              >
                <FormControlLabel
                  value={String(TAXATION_SCHEME_LABELS.STANDARD)}
                  control={<Radio />}
                  label={t('taxation.allSelectedToStandard')}
                  data-testid="taxation-standard"
                />
                <FormControlLabel
                  value={String(TAXATION_SCHEME_LABELS.MARGIN)}
                  control={<Radio />}
                  label={t('taxation.allSelectedToMargin')}
                  data-testid="taxation-margin"
                />
              </RadioGroup>
            </form>
          </div>
        )

      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE: {
        if (parentPage === LISTING_AND_APPRAISAL && showBuyPriceOption) {
          return (
            <div>
              <FormControl>
                <RadioGroup onChange={handleChangeEditPriceType}>
                  <FormControlLabel
                    checked={priceTarget === 'sellPrice'}
                    value="sellPrice"
                    control={<Radio />}
                    label={t('stockPricing.editSellPrice')}
                    data-testid="sell-price"
                  />
                  <FormControlLabel
                    checked={priceTarget === 'buyPrice'}
                    value="buyPrice"
                    control={<Radio />}
                    label={t('stockPricing.editBuyPrice')}
                    data-testid="buy-price"
                  />
                </RadioGroup>
              </FormControl>

              <form onSubmit={handleSubmit}>
                <TextField
                  autoFocus
                  margin="dense"
                  fullWidth
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={placeholder}
                  // variant="outlined"
                  name={type}
                  data-testid="set-price-input"
                />
              </form>
            </div>
          )
        } else {
          return (
            <form onSubmit={handleSubmit}>
              <TextField
                autoFocus
                margin="dense"
                fullWidth
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholder}
                // variant="outlined"
                name={type}
                data-testid="set-price-input"
              />
            </form>
          )
        }
      }

      default:
        return (
          <form onSubmit={handleSubmit}>
            {isLocation && (
              <p className="mb-3 text-muted">{t('stockPricing.bulkEdit.setLocationDefaultHint')}</p>
            )}
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              // variant="outlined"
              name={type}
              helperText={
                type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT
                  ? t('stockPricing.bulkEdit.commentAppendHint')
                  : undefined
              }
            />
            {showUsEditionWarning && (
              <Box width={350} maxWidth="100%">
                <Alert variant="warning" className="mt-3" data-testid="us-edition-comment-warning">
                  <Trans components={{ 1: <b></b> }} i18nKey="card.usEditionCommentWarning" />{' '}
                  {t('stockPricing.bulkEdit.usEditionCommentWarningConfirm')}
                </Alert>
              </Box>
            )}
          </form>
        )
    }
  }

  return (
    <Dialog maxWidth="xl" open={open} onClose={onClose} aria-labelledby="action-editor-title">
      <div className="p-4">
        <DialogTitle disableTypography id="action-editor-title">
          <h3>{title}</h3>
          <IconButton
            aria-label={t('close') as string}
            onClick={onClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>{getContent()}</DialogContent>

        {!isCondition && (
          <DialogActions>
            {type === INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT && showButtonRemoveComment && (
              <Button
                variant="outlined"
                data-testid="remove-comment-btn"
                onClick={() => {
                  setValue(ESCAPE_STRING_FOR_EMPTY_COMMENT)
                  onConfirm({
                    actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT,
                    input: ESCAPE_STRING_FOR_EMPTY_COMMENT,
                  })
                }}
                style={{ marginRight: 'auto' }}
              >
                {t('stockPricing.removeComment')}
              </Button>
            )}

            <Button
              onClick={() => handleSubmit()}
              color="primary"
              variant="contained"
              data-testid="confirm-global-updates"
              disabled={!isLocation && value.trim() === ''}
            >
              {t('common.confirm')}
            </Button>
          </DialogActions>
        )}
      </div>
    </Dialog>
  )
}
